import { DropDownList, DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { FC, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import ClearFilters from 'src/assets/icons/ClearFilters';
import Button from 'src/components/design-system/Button';
import { IsManagerPermissions } from 'src/components/guards/PermissionsList';
import useAuth from 'src/hooks/useAuth';
import useInvoiceScanManager from 'src/hooks/useInvoiceScanManager';
import usePermission from 'src/hooks/usePermissions';
import { modifyRequest } from 'src/utils/requestInterceptor';
import AttachmentDelete from './AttachmentDelete';
import AttachmentUndelete from './AttachmentUndelete';
import {
  invoiceDeleteAvailableCategories,
  invoiceReassignAvailableCategories,
  invoiceRunOcrScanAvailableCategories,
  invoiceUnDeleteAvailableCategories,
  useInvoiceScanManagerCategories,
} from './InvoiceScanManagerCategories';
import ReassignCompany from './ReassignCompany';
import RunOcrScan from './RunOcrScan';
interface GridToolbarProps {
  clearFilter: () => void;
}

const GridToolbar: FC<GridToolbarProps> = ({ clearFilter }) => {
  const {
    dropdownFilter,
    setDropdownFilter,
    setDataState,
    dataState,
    isOcrCompany,
    invoiceScanManagerFilterInitialState,
    setLatestClickedId,
  } = useInvoiceScanManager();
  const categories = useInvoiceScanManagerCategories();
  const { companies } = useAuth();
  const isManager = usePermission({ permissions: IsManagerPermissions });

  const isClearFilterDisabled = useMemo(
    () =>
      !dataState.filter?.filters.length &&
      dropdownFilter.value === invoiceScanManagerFilterInitialState.value,
    [dataState.filter?.filters.length, invoiceScanManagerFilterInitialState, dropdownFilter]
  );

  const handleFilterChange = useCallback(
    (event: DropDownListChangeEvent) => {
      setLatestClickedId(0);
      setDropdownFilter(event.value);
      setDataState((prev) => {
        if (modifyRequest(prev).filters.length) {
          toast.info('Preset filter applied. User-applied filters cleared.');
        }
        return {
          ...prev,
          filter: {
            logic: 'and',
            filters: [],
          },
        };
      });
    },
    [setDataState, setDropdownFilter, setLatestClickedId]
  );

  return (
    <div className="flex flex-wrap items-center justify-between w-full">
      <div className="flex flex-wrap items-center gap-1">
        <Button
          endIcon={<ClearFilters />}
          color="transparent"
          onClick={clearFilter}
          className={isClearFilterDisabled ? '!bg-transparent' : ''}
          disabled={isClearFilterDisabled}
        >
          Clear Filters
        </Button>
        {invoiceDeleteAvailableCategories.includes(dropdownFilter.value) && <AttachmentDelete />}
        {invoiceUnDeleteAvailableCategories.includes(dropdownFilter.value) && (
          <AttachmentUndelete />
        )}
        {invoiceReassignAvailableCategories.includes(dropdownFilter.value) &&
          isManager &&
          companies.length > 1 && <ReassignCompany />}
        {invoiceRunOcrScanAvailableCategories.includes(dropdownFilter.value) && isOcrCompany && (
          <RunOcrScan />
        )}
        <div className="text-white text-sm">Click on file name to rename</div>
      </div>
      <div className="flex flex-wrap items-center gap-1">
        <DropDownList
          id="view-drop-down-list"
          style={{
            width: 175,
          }}
          data={categories}
          textField="label"
          dataItemKey="value"
          value={dropdownFilter}
          onChange={handleFilterChange}
        />
      </div>
    </div>
  );
};

export default GridToolbar;
